import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from './ui/badge';
import { Music, FileText, Copyright, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { ScrollReveal } from './ScrollAnimation';
import { motion } from 'framer-motion';

const Hero = () => {
  const { t } = useLanguage();
  
  // Playlist de chansons exemples - FOR MY CLARA en premier
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const songs = [
    {
      title: "For My Clara",
      artist: "TRAKZSTUDIO",
      url: "/audio/fetes-des-peres.wav"
    },
    {
      title: "Pour ma Khadija",
      artist: "TRAKZSTUDIO",
      url: "https://customer-assets.emergentagent.com/job_compose-translate/artifacts/70uxirpw_Pour%20ma%20Khadija.wav"
    },
    {
      title: "Coup de foudre pour Lila",
      artist: "TRAKZSTUDIO",
      url: "https://customer-assets.emergentagent.com/job_compose-translate/artifacts/livxn858_Coup%20de%20foudre%20pour%20Lila.wav"
    }
  ];

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  // Navigation entre les chansons
  const handleNextSong = () => {
    const nextIndex = (currentSongIndex + 1) % songs.length;
    setCurrentSongIndex(nextIndex);
    setProgress(0);
    setCurrentTime(0);
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handlePreviousSong = () => {
    const prevIndex = currentSongIndex === 0 ? songs.length - 1 : currentSongIndex - 1;
    setCurrentSongIndex(prevIndex);
    setProgress(0);
    setCurrentTime(0);
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(progress);
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const handleProgressClick = (e) => {
    if (audioRef.current && progressBarRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = (clickX / rect.width) * 100;
      const newTime = (percentage / 100) * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setProgress(percentage);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <section id="home" className="relative overflow-hidden min-h-screen">
      {/* Video Background - démarre tout en haut */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="/videos/hero-background.mp4" type="video/mp4" />
        </video>
      </div>
      
      {/* Gradient Overlay léger */}
      <div className="absolute inset-0 z-10 bg-gradient-to-br from-orange-100/40 via-pink-100/40 to-rose-200/40"></div>
      
      {/* Top Badge */}
      <div className="container mx-auto px-4 relative z-20 pt-32 pb-12">
        <ScrollReveal variant="fadeInUp">
          <div className="text-center mb-6">
            <Badge className="bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 text-white border-0 px-4 py-2 text-sm">
              {t('hero.badge')}
            </Badge>
            <p className="mt-3 text-sm text-gray-900 font-bold">
              {t('hero.reviewsBadge')}
            </p>
          </div>
        </ScrollReveal>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Left: Vinyl Player Mockup - Hidden on mobile for performance */}
          <ScrollReveal variant="fadeInLeft" delay={0.2}>
            <div className="relative hidden lg:block">
              <div className="relative w-full max-w-md mx-auto">
                {/* Gradient Background Card */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-3xl blur-3xl"></div>
              
              {/* Premium Player with decorative backgrounds */}
              <div className="relative bg-gradient-to-br from-white to-gray-50 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/50 overflow-hidden">
                {/* Audio element */}
                <audio
                  ref={audioRef}
                  key={currentSongIndex}
                  src={songs[currentSongIndex].url}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleEnded}
                />
                
                {/* Subtle background decorations */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-100/20 to-violet-100/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-fuchsia-100/20 to-pink-100/20 rounded-full blur-3xl"></div>
                
                <div className="relative z-10">
                  {/* Logo Record - Enhanced */}
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className={`w-72 h-72 rounded-full flex items-center justify-center transition-transform duration-700 ${isPlaying ? 'animate-spin-slow' : ''}`}>
                        <img 
                          src="https://customer-assets.emergentagent.com/job_compose-translate/artifacts/s3iampc6_Logo%20Trakz.png"
                          alt="TRAKZSTUDIO Logo"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="absolute inset-0 rounded-full bg-black/10 blur-xl -z-10 scale-95"></div>
                    </div>
                  </div>

                  {/* Song Info avec animation */}
                  <motion.div 
                    key={currentSongIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-8"
                  >
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{songs[currentSongIndex].title}</h3>
                    <p className="text-sm font-medium text-gray-500 tracking-wide">{songs[currentSongIndex].artist}</p>
                    <p className="text-xs text-purple-600 font-semibold mt-1">{currentSongIndex + 1} / {songs.length}</p>
                  </motion.div>

                  {/* Progress with Times */}
                  <div className="mb-8">
                    <div ref={progressBarRef} onClick={handleProgressClick} className="cursor-pointer group mb-3">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden hover:h-2.5 transition-all shadow-inner">
                        <div className="h-full bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 rounded-full transition-all relative shadow-lg" style={{ width: `${progress}%` }}>
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-xl opacity-0 group-hover:opacity-100 transition-all border-2 border-purple-500 scale-0 group-hover:scale-100"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm font-semibold text-gray-600 px-1">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>

                  {/* Controls - Navigation de Playlist Optimisée */}
                  <div className="flex items-center justify-center gap-6 mb-8">
                    {/* Previous Song Button */}
                    <button 
                      onClick={handlePreviousSong} 
                      className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-violet-100 hover:from-purple-200 hover:to-violet-200 text-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95" 
                      title="Chanson précédente"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z"/>
                      </svg>
                    </button>
                    
                    {/* Play/Pause Button - Grand au centre */}
                    <button 
                      onClick={togglePlay} 
                      className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 via-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-2xl hover:shadow-purple-500/50 transform hover:scale-110 active:scale-95 transition-all duration-300" 
                      title={isPlaying ? "Pause" : "Lecture"}
                    >
                      {isPlaying ? (
                        <Pause className="w-9 h-9" fill="white" />
                      ) : (
                        <Play className="w-9 h-9 ml-1" fill="white" />
                      )}
                    </button>
                    
                    {/* Next Song Button */}
                    <button 
                      onClick={handleNextSong} 
                      className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-100 to-pink-100 hover:from-fuchsia-200 hover:to-pink-200 text-fuchsia-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95" 
                      title="Chanson suivante"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 18h2V6h-2v12zM6 18l8.5-6L6 6v12z"/>
                      </svg>
                    </button>
                  </div>

                  {/* Playlist Progress Indicator - Cliquable */}
                  <div className="flex justify-center gap-2 mb-6">
                    {songs.map((song, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentSongIndex(index);
                          setProgress(0);
                          setCurrentTime(0);
                          if (isPlaying && audioRef.current) {
                            audioRef.current.pause();
                            setIsPlaying(false);
                          }
                        }}
                        className={`h-1.5 rounded-full transition-all duration-300 hover:opacity-80 ${
                          index === currentSongIndex 
                            ? 'w-12 bg-gradient-to-r from-purple-600 to-fuchsia-500 shadow-md' 
                            : 'w-6 bg-gray-300 hover:bg-gray-400'
                        }`}
                        title={song.title}
                      />
                    ))}
                  </div>

                  {/* Volume */}
                  <div className="flex items-center justify-center gap-4 px-6">
                    <button onClick={toggleMute} className="w-11 h-11 flex items-center justify-center rounded-full bg-white hover:bg-purple-50 text-gray-600 hover:text-purple-600 transition-all shadow-md hover:shadow-lg" title={isMuted ? "Activer" : "Couper"}>
                      {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                    <div className="flex-1 max-w-xs relative">
                      <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={handleVolumeChange} className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer slider-thumb shadow-inner" style={{background: `linear-gradient(to right, rgb(147 51 234) 0%, rgb(147 51 234) ${(isMuted ? 0 : volume) * 100}%, rgb(229 231 235) ${(isMuted ? 0 : volume) * 100}%, rgb(229 231 235) 100%)`}} />
                    </div>
                    <span className="text-sm font-bold text-gray-700 w-14 text-center bg-white rounded-full px-3 py-1 shadow-md">{Math.round((isMuted ? 0 : volume) * 100)}%</span>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </ScrollReveal>

          {/* Right: Text Content */}
          <ScrollReveal variant="fadeInRight" delay={0.4}>
            <div className="text-center lg:text-left space-y-8">
            <div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                {t('hero.mainTitle').split(' ').map((word, i) => {
                  const purpleWords = ['émotion', 'musique', 'emotion', 'music', 'emoción', 'música', 'emozione', 'musica'];
                  const isPurple = purpleWords.includes(word.toLowerCase().replace(/[.,!?;:]/, ''));
                  return isPurple ? 
                    <span key={i} className="bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">{word} </span> : 
                    word + ' ';
                })}
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-900 leading-relaxed font-bold">
                {t('hero.mainSubtitle')}
              </p>
            </div>

            {/* CTA Button */}
            <div>
              <a
                href="#create"
                className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-xl font-semibold shadow-lg transform hover:scale-105 transition-all duration-300 bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 text-white hover:from-purple-700 hover:via-violet-600 hover:to-fuchsia-600"
              >
                <Music className="w-6 h-6" />
                {t('hero.ctaButton')}
              </a>
              
              <p className="mt-4">
                <span className="font-bold text-lg text-gray-900">2 Personalized songs + QR code:</span> <span className="font-bold text-2xl text-purple-600">{t('hero.price')}</span>
              </p>
              
              {/* Trust Badges - Google & Trustpilot */}
              <div className="flex items-center justify-center lg:justify-start gap-3 mt-6">
                {/* Google Badge */}
                <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                  <svg className="w-5 h-5" viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                  </svg>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-semibold text-gray-700">4.9</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3.5 h-3.5" fill="#FFC107" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Trustpilot Badge */}
                <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#00B67A" d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8-6.2-3.3-6.2 3.3 1.2-6.8-5-4.9 6.9-1z"/>
                  </svg>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-semibold text-gray-700">4.8</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3.5 h-3.5" fill="#00B67A" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-blue-300/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-cyan-300/20 rounded-full blur-3xl"></div>
    </section>
  );
};

export default Hero;
